// src/pages/store-pickup/StorePickupOrdersPage.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Store,
  CreditCard,
  PackageCheck,
  RefreshCw,
  X,
  AlertCircle,
  MonitorSmartphone
} from 'lucide-react';
import { PageHeader, DataTable, FilterPanel } from '../../components/common';
import DateRangePicker from '../../components/ui/DateRangePicker/DateRangePicker';
import Dropdown from '../../components/ui/Dropdown/Dropdown';
import DatePicker from '../../components/ui/DatePicker/DatePicker';
import OrderDetailsModal from '../orders/components/OrderDetailsModal';
import StorePickupPaymentRecordsModal from './components/StorePickupPaymentRecordsModal';
import { getStorePickupOrders, dispatchStorePickupOrder, updateStorePickupDeliveryNotes } from '../../services/api/storePickup';
import { getUserWarehouses } from '../../services/api/packingBatch';
import { getShops } from '../../services/api/shops';
import { useLocalization } from '../../contexts/LocalizationContext';
import { useAlert } from '../../contexts/AlertContext';
import { usePermissions } from '../../hooks/usePermissions';
import { formatCurrency } from '../../utils/currencyUtil';
import styles from './StorePickupOrdersPage.module.css';

const StorePickupOrdersPage = () => {
  const { t } = useLocalization();
  const { success, error: showError } = useAlert();
  const { hasPermission } = usePermissions();
  const canDispatch = hasPermission('dispatch_store_pickup_orders');
  const canBackdateDispatch = hasPermission('backdated_dispatch');
  const canOpenInPos = canDispatch && hasPermission('create_offline_sales_order');
  const navigate = useNavigate();

  const [orders, setOrders] = useState([]);
  const [shops, setShops] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadingError, setLoadingError] = useState(null);
  const [isInitialized, setIsInitialized] = useState(false);

  const [pagination, setPagination] = useState({
    currentPage: 1,
    pageSize: 20,
    total: 0,
    pageSizeOptions: [10, 20, 50, 100]
  });

  const [filters, setFilters] = useState({
    shopIds: [],
    dateRange: { from: '', to: '' },
    search: ''
  });
  const [showFilters, setShowFilters] = useState(false);

  const [selectedOrderDetails, setSelectedOrderDetails] = useState(null);
  const [paymentRecordsOrder, setPaymentRecordsOrder] = useState(null);
  const [notesBuffer, setNotesBuffer] = useState({});
  const [savingNotesId, setSavingNotesId] = useState(null);
  const [dispatchOrder, setDispatchOrder] = useState(null);
  const [dispatchForm, setDispatchForm] = useState({ warehouseId: '', dispatchedDate: new Date().toISOString().split('T')[0] });
  const [isDispatching, setIsDispatching] = useState(false);
  const [posShopPickerOrder, setPosShopPickerOrder] = useState(null);
  const [posShops, setPosShops] = useState(null);
  const [selectedPosShopId, setSelectedPosShopId] = useState('');

  useEffect(() => {
    const loadReferenceData = async () => {
      try {
        const [shopsData, warehousesData] = await Promise.all([getShops(), getUserWarehouses()]);
        setShops(Array.isArray(shopsData) ? shopsData : (shopsData.shops || []));
        setWarehouses(warehousesData.warehouses || []);
      } catch (err) {
        console.error('Failed to load reference data:', err);
      }
    };
    loadReferenceData();
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => setIsInitialized(true), 0);
    return () => clearTimeout(timer);
  }, []);

  const fetchOrders = useCallback(async () => {
    try {
      setIsLoading(true);
      setLoadingError(null);

      const response = await getStorePickupOrders({
        page: pagination.currentPage,
        limit: pagination.pageSize,
        shopIds: filters.shopIds.length > 0 ? filters.shopIds.join(',') : undefined,
        dateFrom: filters.dateRange?.from || undefined,
        dateTo: filters.dateRange?.to || undefined,
        search: filters.search || undefined
      });

      setOrders(response.orders || []);
      setPagination((prev) => ({ ...prev, total: response.total || 0 }));
    } catch (err) {
      console.error('Failed to fetch store pickup orders:', err);
      if (err.response?.status === 403) {
        setLoadingError(t('errors.permissionDenied'));
      } else {
        setLoadingError(t('storePickup.failedToLoad'));
      }
    } finally {
      setIsLoading(false);
    }
  }, [pagination.currentPage, pagination.pageSize, filters, t]);

  useEffect(() => {
    if (!isInitialized) return;
    fetchOrders();
  }, [isInitialized, fetchOrders]);

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  };

  const handleSearchChange = (value) => {
    setFilters((prev) => ({ ...prev, search: value }));
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  };

  const handleClearFilters = () => {
    setFilters({ shopIds: [], dateRange: { from: '', to: '' }, search: '' });
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  };

  const handlePageSizeChange = (newPageSize) => {
    setPagination((prev) => ({ ...prev, pageSize: newPageSize, currentPage: 1 }));
  };

  const handleOrderUpdate = (updatedOrder) => {
    setOrders((prev) => prev.map((o) => (o.id === updatedOrder.id ? { ...o, ...updatedOrder } : o)));
  };

  const handleSaveDeliveryNotes = async (order) => {
    const value = notesBuffer[order.id];
    if (value === undefined || value === (order.deliveryNotes || '')) return;

    setSavingNotesId(order.id);
    try {
      await updateStorePickupDeliveryNotes(order.id, value);
      setOrders((prev) => prev.map((o) => (o.id === order.id ? { ...o, deliveryNotes: value } : o)));
      setNotesBuffer((prev) => {
        const next = { ...prev };
        delete next[order.id];
        return next;
      });
    } catch (err) {
      console.error('Failed to update delivery notes:', err);
      showError(err.response?.data?.message || t('storePickup.deliveryNotesSaveFailed'));
    } finally {
      setSavingNotesId(null);
    }
  };

  const openDispatchModal = (order) => {
    setDispatchForm({ warehouseId: warehouses.length === 1 ? warehouses[0].id : '', dispatchedDate: new Date().toISOString().split('T')[0] });
    setDispatchOrder(order);
  };

  const handleDispatchSubmit = async (e) => {
    e.preventDefault();
    if (!dispatchForm.warehouseId) {
      showError(t('storePickup.warehouseRequired'));
      return;
    }

    setIsDispatching(true);
    try {
      await dispatchStorePickupOrder(dispatchOrder.id, {
        warehouseId: dispatchForm.warehouseId,
        dispatchedDate: dispatchForm.dispatchedDate
      });
      setOrders((prev) => prev.filter((o) => o.id !== dispatchOrder.id));
      setDispatchOrder(null);
      success(t('storePickup.dispatchSuccess'));
    } catch (err) {
      console.error('Failed to dispatch store pickup order:', err);
      showError(err.response?.data?.message || t('storePickup.dispatchFailed'));
    } finally {
      setIsDispatching(false);
    }
  };

  const openOrderInPos = (posShopId, order) => {
    navigate(`/offline-sales/terminal/${posShopId}?storePickupOrderId=${order.id}`);
  };

  // The terminal is per POS shop: go straight there when the user has one, else let them pick
  const handleOpenInPos = async (order) => {
    try {
      let shopsList = posShops;
      if (!shopsList) {
        const data = await getShops({ type: 'pos', assigned: true, limit: 50 });
        shopsList = data.shops || (Array.isArray(data) ? data : []);
        setPosShops(shopsList);
      }
      if (shopsList.length === 0) {
        showError(t('storePickup.noPosShop'));
      } else if (shopsList.length === 1) {
        openOrderInPos(shopsList[0].id, order);
      } else {
        setSelectedPosShopId(shopsList.some((s) => s.id === order.shopId) ? order.shopId : '');
        setPosShopPickerOrder(order);
      }
    } catch (err) {
      console.error('Failed to load POS shops:', err);
      showError(err.response?.data?.message || t('storePickup.noPosShop'));
    }
  };

  const filterDefinitions = [
    {
      key: 'shopIds',
      label: t('common.shop'),
      type: 'multiselect',
      placeholder: t('ecomOrders.allShops'),
      options: shops,
      displayKey: 'name',
      valueKey: 'id',
      searchKey: 'name'
    },
    {
      key: 'dateRange',
      label: t('storePickup.orderDate'),
      type: 'custom',
      render: (value, onChange) => (
        <DateRangePicker value={value || { from: '', to: '' }} onChange={onChange} />
      )
    }
  ];

  const columns = [
    {
      key: 'orderNumber',
      header: t('storePickup.orderNumber'),
      width: '130px',
      render: (order) => (
        <Link to={`/orders/${order.id}`} className={styles.orderLink} onClick={(e) => e.stopPropagation()}>
          {order.orderNumber}
        </Link>
      )
    },
    {
      key: 'customerName',
      header: t('storePickup.customerName'),
      render: (order) => order.customerName || '-'
    },
    {
      key: 'phone',
      header: t('storePickup.phone'),
      width: '140px',
      render: (order) => order.phoneNumbers?.find((p) => p.isPrimary)?.phoneNumber || order.phoneNumbers?.[0]?.phoneNumber || '-'
    },
    {
      key: 'shop',
      header: t('common.shop'),
      width: '140px',
      render: (order) => order.Shop?.name || '-'
    },
    {
      key: 'grandTotal',
      header: t('ecomOrders.total'),
      align: 'right',
      width: '120px',
      render: (order) => <div className={styles.totalAmount}>{formatCurrency(order.grandTotal)}</div>
    },
    {
      key: 'deliveryNotes',
      header: t('storePickup.deliveryNotes'),
      render: (order) => (
        <input
          type="text"
          className={styles.notesInput}
          value={notesBuffer[order.id] ?? (order.deliveryNotes || '')}
          placeholder={t('storePickup.deliveryNotesPlaceholder')}
          disabled={savingNotesId === order.id}
          onClick={(e) => e.stopPropagation()}
          onChange={(e) => setNotesBuffer((prev) => ({ ...prev, [order.id]: e.target.value }))}
          onBlur={() => handleSaveDeliveryNotes(order)}
          onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
        />
      )
    },
    {
      key: 'status',
      header: t('ecomOrders.status'),
      width: '150px',
      render: (order) => (
        <div
          className={styles.statusBadge}
          style={{
            backgroundColor: order.OrderStatus?.colorCode ? `${order.OrderStatus.colorCode}20` : '#F59E0B20',
            borderColor: order.OrderStatus?.colorCode || '#F59E0B',
            color: order.OrderStatus?.colorCode || '#92400e'
          }}
        >
          <span>{order.OrderStatus?.label}</span>
        </div>
      )
    }
  ];

  const tableActions = {
    width: '150px',
    frozen: true,
    render: (order) => (
      <div className={styles.actionsWrapper}>
        <button
          type="button"
          className={styles.actionButton}
          title={t('storePickup.viewPaymentRecords')}
          onClick={(e) => { e.stopPropagation(); setPaymentRecordsOrder(order); }}
        >
          <CreditCard size={18} />
        </button>
        {canOpenInPos && (
          <button
            type="button"
            className={styles.actionButton}
            title={t('storePickup.openInPos')}
            onClick={(e) => { e.stopPropagation(); handleOpenInPos(order); }}
          >
            <MonitorSmartphone size={18} />
          </button>
        )}
        {canDispatch && (
          <button
            type="button"
            className={`${styles.actionButton} ${styles.dispatchButton}`}
            title={order.hasUnverifiedPayment ? t('storePickup.dispatchBlockedUnverified') : t('storePickup.dispatch')}
            disabled={order.hasUnverifiedPayment}
            onClick={(e) => { e.stopPropagation(); openDispatchModal(order); }}
          >
            <PackageCheck size={18} />
          </button>
        )}
      </div>
    )
  };

  const isBackdated = dispatchForm.dispatchedDate < new Date().toISOString().split('T')[0];

  if (loadingError) {
    return (
      <div className={styles.storePickupPage}>
        <div className={styles.errorState}>
          <h3>{loadingError}</h3>
          <button onClick={fetchOrders} className={styles.retryButton}>
            <RefreshCw size={16} />
            {t('common.retry')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.storePickupPage}>
      <PageHeader
        title={t('storePickup.pageTitle')}
        description={t('storePickup.pageDescription')}
        icon={Store}
      />

      <FilterPanel
        filters={filterDefinitions}
        values={filters}
        onChange={handleFilterChange}
        onClear={handleClearFilters}
        show={showFilters}
        onToggle={() => setShowFilters(!showFilters)}
        searchConfig={{
          show: true,
          value: filters.search,
          onChange: handleSearchChange,
          placeholder: t('storePickup.searchPlaceholder')
        }}
        pageSizeConfig={{
          show: true,
          value: pagination.pageSize,
          onChange: handlePageSizeChange,
          options: pagination.pageSizeOptions
        }}
      />

      <DataTable
        data={orders}
        columns={columns}
        actions={tableActions}
        onRowClick={(order) => setSelectedOrderDetails(order)}
        pagination={pagination}
        onPaginationChange={setPagination}
        loading={isLoading}
        emptyMessage={t('storePickup.noOrdersFound')}
        emptyDescription={t('storePickup.noOrdersFoundDescription')}
      />

      {selectedOrderDetails && (
        <OrderDetailsModal
          order={selectedOrderDetails}
          onClose={() => setSelectedOrderDetails(null)}
          onUpdate={handleOrderUpdate}
        />
      )}

      {paymentRecordsOrder && (
        <StorePickupPaymentRecordsModal
          order={paymentRecordsOrder}
          onClose={() => setPaymentRecordsOrder(null)}
          onVerificationChange={(orderId, hasUnverifiedPayment) =>
            setOrders((prev) => prev.map((o) => (o.id === orderId ? { ...o, hasUnverifiedPayment } : o)))
          }
        />
      )}

      {posShopPickerOrder && (
        <div className={styles.dispatchModalOverlay} onClick={(e) => e.target === e.currentTarget && setPosShopPickerOrder(null)}>
          <div className={styles.dispatchModalContent}>
            <div className={styles.dispatchModalHeader}>
              <h3>{t('storePickup.openInPos')} #{posShopPickerOrder.orderNumber}</h3>
              <button onClick={() => setPosShopPickerOrder(null)} className={styles.closeButton}>
                <X size={18} />
              </button>
            </div>
            <form
              className={styles.dispatchModalBody}
              onSubmit={(e) => {
                e.preventDefault();
                if (selectedPosShopId) openOrderInPos(selectedPosShopId, posShopPickerOrder);
              }}
            >
              <div className={styles.formGroup}>
                <label>{t('storePickup.posShop')}</label>
                <Dropdown
                  value={selectedPosShopId}
                  onChange={setSelectedPosShopId}
                  options={posShops || []}
                  displayKey="name"
                  valueKey="id"
                  placeholder={t('storePickup.selectPosShop')}
                  allowClear={false}
                />
              </div>
              <div className={styles.dispatchModalActions}>
                <button type="button" onClick={() => setPosShopPickerOrder(null)} className={styles.cancelButton}>
                  {t('common.cancel')}
                </button>
                <button type="submit" className={styles.confirmButton} disabled={!selectedPosShopId}>
                  {t('storePickup.open')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {dispatchOrder && (
        <div className={styles.dispatchModalOverlay} onClick={(e) => e.target === e.currentTarget && setDispatchOrder(null)}>
          <div className={styles.dispatchModalContent}>
            <div className={styles.dispatchModalHeader}>
              <h3>{t('storePickup.dispatchOrder')} #{dispatchOrder.orderNumber}</h3>
              <button onClick={() => setDispatchOrder(null)} className={styles.closeButton}>
                <X size={18} />
              </button>
            </div>
            <form onSubmit={handleDispatchSubmit} className={styles.dispatchModalBody}>
              <div className={styles.formGroup}>
                <label>{t('storePickup.warehouse')}</label>
                <Dropdown
                  value={dispatchForm.warehouseId}
                  onChange={(v) => setDispatchForm((prev) => ({ ...prev, warehouseId: v }))}
                  options={warehouses}
                  displayKey="name"
                  valueKey="id"
                  placeholder={t('storePickup.selectWarehouse')}
                  allowClear={false}
                />
              </div>
              <div className={styles.formGroup}>
                <label>{t('storePickup.dispatchedDate')}</label>
                <DatePicker
                  value={dispatchForm.dispatchedDate}
                  onChange={(d) => setDispatchForm((prev) => ({ ...prev, dispatchedDate: d }))}
                  maxDate={canBackdateDispatch ? undefined : new Date().toISOString().split('T')[0]}
                />
                {isBackdated && !canBackdateDispatch && (
                  <span className={styles.warningText}>
                    <AlertCircle size={12} />
                    {t('storePickup.backdateNotAllowed')}
                  </span>
                )}
              </div>
              <div className={styles.dispatchModalActions}>
                <button type="button" onClick={() => setDispatchOrder(null)} className={styles.cancelButton}>
                  {t('common.cancel')}
                </button>
                <button
                  type="submit"
                  className={styles.confirmButton}
                  disabled={isDispatching || (isBackdated && !canBackdateDispatch)}
                >
                  {isDispatching ? t('common.processing') || 'Processing...' : t('storePickup.dispatch')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default StorePickupOrdersPage;
